import React from 'react';
import { Alert, Box, Button, Chip, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Zuordnungsoption } from '../api/vorlagenZuordnungOptionen';
import {
  VORLAGEN_ARTEN,
  VORLAGEN_GESCHLECHTER,
  VorlagenZuordnungForm,
  Zuordnungsfehler,
} from '../services/vorlagenZuordnung';

interface Props {
  hinweis?: React.ReactNode;
  wert: VorlagenZuordnungForm;
  onChange: (wert: VorlagenZuordnungForm) => void;
  fehler: Zuordnungsfehler;
  gesperrt: boolean;
  optionen: {
    firmen: Zuordnungsoption[];
    vertragsarten: Zuordnungsoption[];
    laedt: boolean;
    fehler: boolean;
    erneutLaden: () => void;
  };
}

/** Platzhalter im Hinweis, abgesetzt wie in der freigegebenen Skizze (Schreibmaschinenschrift auf hellblauem Grund). */
function Platzhalter({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="code"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.8125rem',
        whiteSpace: 'nowrap',
        borderRadius: 0.5,
        px: 0.5,
        color: (theme) =>
          theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.dark,
        bgcolor: (theme) => alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.16 : 0.1),
      }}
    >
      {children}
    </Box>
  );
}

/** Die Zuordnung ist unabhängig von der Logo-Auswahl im Vertragsdokument. */
export default function VorlagenZuordnung({ wert, onChange, fehler, gesperrt, optionen, hinweis }: Props) {
  const auswahl = (feld: keyof VorlagenZuordnungForm, label: string, inhalt: React.ReactNode) => (
    <TextField
      select
      required
      fullWidth
      size="small"
      id={`vorlagen-zuordnung-${feld}`}
      label={label}
      value={wert[feld]}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ 'aria-label': label }}
      disabled={gesperrt || ((feld === 'firma' || feld === 'vertragsart') && (optionen.laedt || optionen.fehler))}
      error={!!fehler[feld]}
      helperText={fehler[feld]}
      onChange={(event) => {
        const neu = { ...wert, [feld]: event.target.value };
        if (feld === 'firma' && neu.firma !== '1' && neu.vertragsart === '3') neu.vertragsart = '';
        if (feld === 'art' && neu.art !== 'contract' && neu.fuer === 'company') neu.fuer = '';
        onChange(neu);
      }}
    >
      <option value="">Bitte auswählen</option>
      {inhalt}
    </TextField>
  );
  return (
    <Box
      component="section"
      aria-labelledby="vorlagen-zuordnung-titel"
      sx={{ border: 2, borderColor: '#3d66ff', borderRadius: 2, p: 2, mb: 2 }}
    >
      <Typography id="vorlagen-zuordnung-titel" component="h2" variant="subtitle1" sx={{ mb: 2 }}>
        Wofür wird diese Vorlage genutzt?{' '}
        <Chip size="small" label="NEU" sx={{ color: '#82a5ff', bgcolor: '#223353', borderRadius: 1 }} />
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {auswahl(
          'art',
          'Art',
          Object.entries(VORLAGEN_ARTEN).map(([id, text]) => (
            <option key={id} value={id}>
              {text}
            </option>
          )),
        )}
        {auswahl(
          'firma',
          'Firma',
          <>
            {wert.firma && !optionen.firmen.some((firma) => String(firma.id) === wert.firma) && (
              <option value={wert.firma}>Gespeicherte Firma (nicht verfügbar)</option>
            )}
            {optionen.firmen.map((firma) => (
              <option key={firma.id} value={firma.id}>
                {firma.title}
              </option>
            ))}
          </>,
        )}
        {auswahl(
          'vertragsart',
          'Vertragsart',
          <>
            {wert.vertragsart && !optionen.vertragsarten.some((art) => String(art.id) === wert.vertragsart) && (
              <option value={wert.vertragsart}>Gespeicherte Vertragsart (nicht verfügbar)</option>
            )}
            {optionen.vertragsarten.map((art) => (
              <option key={art.id} value={art.id} disabled={Number(art.id) === 3 && wert.firma !== '1'}>
                {art.title}
              </option>
            ))}
          </>,
        )}
        {auswahl(
          'fuer',
          'Für',
          Object.entries(VORLAGEN_GESCHLECHTER)
            .filter(([id]) => id !== 'company' || wert.art === 'contract')
            .map(([id, text]) => (
              <option key={id} value={id}>
                {text}
              </option>
            )),
        )}
      </Box>
      {/* RITA-1584: Nur bei „Berater/in – automatisch“ ein Hinweis, für alle Arten; sonst bleibt die Stelle leer.
          Die Platzhalter heißen wie die Knöpfe im Editor, damit man sie dort wiederfindet. */}
      {wert.fuer === 'both' && (
        <Typography id="vorlagen-zuordnung-anrede-hinweis" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Für die Anrede bitte die Platzhalter <Platzhalter>{'{{ANREDE}}'}</Platzhalter> (Frau/Herr) und{' '}
          <Platzhalter>{'{{PERSOENLICHE_ANREDE}}'}</Platzhalter> (Liebe Anna/Lieber Max) einfügen. Den übrigen Text so
          schreiben, dass er für Frauen und Männer passt.
        </Typography>
      )}
      {hinweis}
      {optionen.laedt && (
        <Typography role="status" variant="body2" sx={{ mt: 1 }}>
          Firmen und Vertragsarten werden geladen …
        </Typography>
      )}
      {optionen.fehler && (
        <Alert
          severity="error"
          sx={{ mt: 1 }}
          action={
            <Button color="inherit" onClick={optionen.erneutLaden}>
              Erneut versuchen
            </Button>
          }
        >
          Firmen und Vertragsarten konnten nicht geladen werden. Die gespeicherte Zuordnung bleibt erhalten.
        </Alert>
      )}
    </Box>
  );
}
